import React, { useState, useCallback } from 'react'
import type { JSX } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAppAuth'
import { useApi } from '@/hooks/useAppApi'
import { logger } from '@/utils/logger'
import { markLoggingOut } from '@/lib/logout-flag'
import type { ButtonProps } from '@/components/ui/button'

/**
 * Props for LogoutButton component
 */
interface LogoutButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** Custom button text (default: "Sign Out") */
  children?: React.ReactNode
  /** Text to display during loading state */
  loadingText?: string
  /** Whether to perform global sign-out (all devices) */
  global?: boolean
  /** Whether to show confirmation dialog for global sign-out */
  confirmGlobalSignOut?: boolean
  /** Whether to show error messages inline */
  showErrors?: boolean
  /** Whether to show user information */
  showUserInfo?: boolean
  /** Whether to hide the button when user is not authenticated */
  hideWhenNotAuthenticated?: boolean
  /** Button variant - standard button variants */
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  /** Callback fired when sign-out process starts */
  onSignOutStart?: () => void
  /** Callback fired when sign-out completes successfully */
  onSignOutComplete?: () => void
  /** Callback fired when sign-out fails */
  onSignOutError?: (error: Error) => void
}

/**
 * Confirmation dialog props
 */
interface ConfirmationDialogProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

/**
 * Simple confirmation dialog for global sign-out
 */
const ConfirmationDialog = ({ isOpen, onConfirm, onCancel, isLoading }: ConfirmationDialogProps): JSX.Element | null => {
  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <h2 id="confirm-dialog-title" className="text-lg font-semibold">
            Sign Out of All Devices?
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <p id="confirm-dialog-description" className="text-sm text-muted-foreground">
            This will sign you out of all devices and sessions. You'll need to sign in again on each device.
          </p>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Signing Out...' : 'Confirm'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * User info display component
 */
const UserInfo = (): JSX.Element => {
  const { user } = useApi() // Get FULL user data from useApi, not useAuth!
  
  if (!user) return <></>
  
  return (
    <div className="text-sm space-y-1">
      <div className="font-medium">{user.name || 'User'}</div>
      <div className="text-muted-foreground">{user.email || ''}</div>
    </div>
  )
}

/**
 * Dropdown menu variant
 */
const LogoutDropdown = (props: LogoutButtonProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  
  return (
    <div className="relative">
      <Button
        variant="ghost" 
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={props.className}
      >
        {props.showUserInfo && <UserInfo />}
        {props.children || 'Account'}
      </Button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-48 bg-popover border rounded-md shadow-md">
            <div className="p-2">
              {props.showUserInfo && (
                <div className="px-2 py-1.5 border-b mb-2">
                  <UserInfo />
                </div>
              )}
              <LogoutButtonCore 
                {...props} 
                variant="ghost" 
                className="w-full justify-start text-sm h-8"
                role="menuitem"
                onClick={() => setIsOpen(false)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Core logout button functionality
 */
const LogoutButtonCore = ({
  children = 'Sign Out',
  loadingText = 'Signing Out...',
  global = false,
  confirmGlobalSignOut = false,
  showErrors = true,
  onSignOutStart,
  onSignOutComplete,
  onSignOutError,
  disabled,
  className,
  onClick,
  ...buttonProps
}: LogoutButtonProps & { onClick?: () => void }): JSX.Element => {
  const { signoutRedirect, isLoading: authLoading, error: authError } = useAuth()
  const { user } = useApi() // Get full app user data from useApi
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  /**
   * Handle sign-out process
   */
  const handleSignOut = useCallback(async (): Promise<void> => {
    try {
      // Clear any previous errors
      setLocalError(null)
      
      // Set loading state
      setIsSigningOut(true)
      
      // Notify callback
      onSignOutStart?.()

      // Mark logout in progress BEFORE redirecting so the login route skips its
      // auto-redirect and doesn't cancel the in-flight Cognito /logout.
      markLoggingOut()

      // Perform sign-out redirect (global sign-out uses empty object, local uses no parameters)
      if (global) {
        await signoutRedirect({})
      } else {
        await signoutRedirect()
      }
      
      // Note: signoutRedirect() initiates a redirect, so onSignOutComplete typically won't be called
      // as the user will be navigated away from this page. However, we call it in case
      // the redirect fails or is cancelled.
      onSignOutComplete?.()
      
    } catch (error) {
      // Handle sign-out error (store locally for display)
      const errorMessage = error instanceof Error ? error.message : 'Sign-out failed'
      setLocalError(errorMessage)
      
      // Notify error callback
      if (error instanceof Error) {
        onSignOutError?.(error)
      } else {
        onSignOutError?.(new Error(errorMessage))
      }
      
      logger.error('Sign-out failed', error instanceof Error ? error : undefined, {
        userId: user?.id,
        email: user?.email,
        global,
      })
      
    } finally {
      // Clear loading state and confirmation dialog
      setIsSigningOut(false)
      setShowConfirmation(false)
    }
  }, [signoutRedirect, global, onSignOutStart, onSignOutComplete, onSignOutError, user?.id, user?.email])

  /**
   * Handle button click - show confirmation for global sign-out if needed
   */
  const handleClick = useCallback((): void => {
    onClick?.()
    
    if (global && confirmGlobalSignOut) {
      setShowConfirmation(true)
    } else {
      handleSignOut()
    }
  }, [global, confirmGlobalSignOut, handleSignOut, onClick])

  /**
   * Handle keyboard interactions
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>): void => {
    // Handle Enter and Space keys for accessibility
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }, [handleClick])

  // Determine loading state
  const isLoading = isSigningOut || authLoading
  
  // Determine error to display (local error or auth context error converted to string)
  const displayError = localError || (authError ? authError.message : null)
  
  // Determine if button should be disabled
  const isDisabled = disabled || isLoading

  return (
    <>
      <Button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={
          isLoading 
            ? `${loadingText} Please wait` 
            : `${children} ${user?.name || 'user'} - Signs out current session${global ? ' and all devices' : ''}`
        }
        className={className}
        {...buttonProps}
      >
        {isLoading ? loadingText : children}
      </Button>
      
      {/* Error message display */}
      {showErrors && displayError && (
        <div 
          className="text-sm text-destructive mt-2" 
          role="alert" 
          aria-live="assertive"
          data-testid="logout-error"
        >
          {displayError}
        </div>
      )}
      
      {/* Confirmation dialog for global sign-out */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        onConfirm={handleSignOut}
        onCancel={() => setShowConfirmation(false)}
        isLoading={isSigningOut}
      />
    </>
  )
}

/**
 * Logout button component that initiates the sign-out flow
 * 
 * Features:
 * - Local or global sign-out options
 * - Confirmation dialog for global sign-out
 * - Loading states with accessible feedback
 * - Error handling and display
 * - User information display
 * - Dropdown variant for navigation bars
 * - Keyboard accessibility support
 * - Automatically hides when user is not authenticated
 * 
 * @example Basic usage
 * ```tsx
 * <LogoutButton />
 * ```
 * 
 * @example Global sign-out with confirmation
 * ```tsx
 * <LogoutButton global confirmGlobalSignOut />
 * ```
 * 
 * @example Dropdown variant with user info
 * ```tsx
 * <LogoutButton variant="dropdown" showUserInfo />
 * ```
 * 
 * @example With callbacks
 * ```tsx
 * <LogoutButton 
 *   onSignOutStart={() => analytics.track('logout_started')}
 *   onSignOutComplete={() => redirect('/login')}
 *   onSignOutError={(error) => showNotification(error.message)}
 * />
 * ```
 */
export const LogoutButton = ({
  hideWhenNotAuthenticated = true,
  showUserInfo = false,
  variant,
  ...props
}: LogoutButtonProps): JSX.Element | null => {
  const { isAuthenticated } = useAuth()

  // Don't render if user is not authenticated (unless explicitly configured)
  if (hideWhenNotAuthenticated && !isAuthenticated) {
    return null
  }

  // Always render the core component - let it handle its own loading states
  return (
    <div className="space-y-2">
      {showUserInfo && <UserInfo />}
      <LogoutButtonCore {...props} variant={variant || 'default'} />
    </div>
  )
}

/**
 * Compact logout button without error display or user info
 * Useful for navigation bars or inline usage
 */
export const LogoutButtonCompact = (props: Omit<LogoutButtonProps, 'showErrors' | 'showUserInfo'>): JSX.Element | null => {
  return <LogoutButton {...props} showErrors={false} showUserInfo={false} />
}

/**
 * Logout button with outline variant and custom styling
 */
export const LogoutButtonOutline = (props: LogoutButtonProps): JSX.Element | null => {
  return <LogoutButton variant="outline" {...props} />
}

/**
 * Small logout button for tight spaces
 */
export const LogoutButtonSmall = (props: LogoutButtonProps): JSX.Element | null => {
  return <LogoutButton size="sm" {...props} />
}

/**
 * Dropdown logout button for navigation menus
 */
export const LogoutButtonDropdown = (props: Omit<LogoutButtonProps, 'variant'>): JSX.Element | null => {
  return <LogoutDropdown {...props} showUserInfo />
}

/**
 * Hook for programmatic logout triggering
 * Useful when you need to trigger logout from non-button elements
 * 
 * @returns Object with logout function and state
 * 
 * @example
 * ```tsx
 * const { triggerLogout, isLoading, error } = useLogout({
 *   global: true,
 *   onComplete: () => navigate('/login')
 * })
 * 
 * const handleSessionExpired = () => {
 *   triggerLogout()
 * }
 * ```
 */
export const useLogout = (options?: {
  global?: boolean
  onStart?: () => void
  onComplete?: () => void
  onError?: (error: Error) => void
}): {
  triggerLogout: () => Promise<void>
  isLoading: boolean
  error: string | null
} => {
  const { signoutRedirect } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const triggerLogout = useCallback(async (): Promise<void> => {
    try {
      setError(null)
      setIsLoading(true)
      
      options?.onStart?.()

      // Mark logout in progress BEFORE redirecting so the login route skips its
      // auto-redirect and doesn't cancel the in-flight Cognito /logout.
      markLoggingOut()

      // Handle global sign-out using react-oidc-context pattern
      if (options?.global) {
        await signoutRedirect({})
      } else {
        await signoutRedirect()
      }
      
      options?.onComplete?.()
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign-out failed'
      setError(errorMessage)
      
      if (err instanceof Error) {
        options?.onError?.(err)
      } else {
        options?.onError?.(new Error(errorMessage))
      }
      
    } finally {
      setIsLoading(false)
    }
  }, [signoutRedirect, options])

  return {
    triggerLogout,
    isLoading,
    error,
  }
}
